import React, { useState } from 'react';
import { Mail } from 'lucide-react';
import { sendNewsletter } from '../../services/newsletterApi';

const Settings = () => {
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const [targetAudience, setTargetAudience] = useState('all');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');

    if (!subject.trim() || !content.trim()) {
      setError('Please enter both subject and content for the newsletter.');
      return;
    }

    setLoading(true);
    try {
      await sendNewsletter({ subject, content, targetAudience });
      setMessage('Newsletter queued to be sent to subscribers.');
      setSubject('');
      setContent('');
      setTargetAudience('all');
    } catch (err) {
      console.error('Error sending newsletter:', err);
      setError(err.message || 'Failed to send newsletter.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            Newsletter Settings
          </h2>
          <p className="text-gray-600">
            Compose and send a newsletter to your subscribers.
          </p>
        </div>
        <div className="p-3 bg-blue-100 rounded-full">
          <Mail className="w-6 h-6 text-blue-600" />
        </div>
      </div>

      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-4"
      >
        {error && <div className="text-sm text-red-600">{error}</div>}
        {message && <div className="text-sm text-green-600">{message}</div>}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Subject
          </label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 shadow-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black text-sm"
            placeholder="Newsletter subject"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Content
          </label>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 shadow-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black text-sm h-40 resize-vertical"
            placeholder="Write your newsletter content here..."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Target audience
          </label>
          <select
            value={targetAudience}
            onChange={(e) => setTargetAudience(e.target.value)}
            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 shadow-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black text-sm"
          >
            <option value="all">All active subscribers</option>
            <option value="new">New subscribers (last 30 days)</option>
            <option value="inactive">Inactive subscribers</option>
          </select>
          <p className="mt-1 text-xs text-gray-500">
            Audience targeting is handled on the server based on subscriber
            activity.
          </p>
        </div>

        <div className="pt-2 flex justify-end">
          <button
            type="submit"
            disabled={loading}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-black hover:bg-gray-900 disabled:opacity-50"
          >
            {loading ? 'Sending...' : 'Send Newsletter'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default Settings;
